import React, { useState, useEffect, useMemo } from 'react';
import LeftMenu from '../components/LeftMenu';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import axios from 'axios';
import { BASE_URL, BATCH_STOCK } from '../utlilities/config';
import withAuth from '../common/auth';
import { getWebUserInfo, initializeWebUserInfo } from '../utlilities/webConfig';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Icons
import { 
  FiSearch, FiFilter, FiRefreshCw, FiDownload, 
  FiAlertCircle, FiCheckCircle, FiPackage, FiCalendar,
  FiBox, FiBarChart2, FiClock, FiInfo, FiChevronDown, FiChevronRight
} from "react-icons/fi";

// Animated loader component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading inventory data...</p>
    </div>
  </div>
);

// No Data component
const NoData = () => (
  <div className="flex flex-col items-center justify-center h-64 py-10">
    <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center text-black">
      <FiBox size={32} />
    </div>
    <h3 className="text-lg font-medium text-black">No batch stock found</h3>
    <p className="text-sm text-black mt-1 mb-6">Try adjusting your search or filters</p>
  </div>
);

// Expiry Badge component
const ExpiryBadge = ({ expiryDate }) => {
  if (!expiryDate) return null;
  
  const today = new Date();
  const expiry = new Date(expiryDate);
  const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  
  let bgColor, textColor, icon, text;
  
  if (daysUntilExpiry <= 0) {
    bgColor = 'bg-red-100';
    textColor = 'text-red-800';
    icon = <FiAlertCircle size={14} className="mr-1" />;
    text = 'Expired';
  } else if (daysUntilExpiry <= 30) {
    bgColor = 'bg-amber-100';
    textColor = 'text-amber-800';
    icon = <FiClock size={14} className="mr-1" />;
    text = 'Expiring soon';
  } else {
    bgColor = 'bg-green-100';
    textColor = 'text-green-800';
    icon = <FiCheckCircle size={14} className="mr-1" />;
    text = 'Valid';
  }
  
  return (
    <span className={`${bgColor} ${textColor} px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center`}>
      {icon}
      {text}
    </span>
  );
};

// Stock Level Indicator


function BatchStock() {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  
  // Close download options when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDownloadOptions && !event.target.closest('.relative')) {
        setShowDownloadOptions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadOptions]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [excludeZeroStock, setExcludeZeroStock] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const navigate = useNavigate();
  
  // User permissions
  const [userRoles, setUserRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  
  // Stock summary
  const [stockSummary, setStockSummary] = useState({
    totalItems: 0,
    totalStock: 0,
    lowStockItems: 0,
    expiringItems: 0
  });
  
  const token = localStorage.getItem('accessToken');

  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;
  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }


  useEffect(() => {
    // Event listener for shortcuts
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        navigate('/testentry');
      } else if (e.ctrlKey && e.key === 'o') {
        e.preventDefault();
        navigate('/patients');
      } else if (e.ctrlKey && e.key === 't') {
        e.preventDefault();
        navigate('/tests');
      } else if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        navigate('/reports');
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate]);

  // Get user roles and permissions
  useEffect(() => {
    const fetchData = async () => {
      let userRolesData;
      if (isElectron()) {
        userRolesData = await ipcRenderer.invoke('store-get', 'user_role');
      } else {
        initializeWebUserInfo();
        userRolesData = getWebUserInfo().user_role;
      }
      setUserRoles(userRolesData);
      setPermissions(processPermissions(userRolesData));
    };
    fetchData();
  }, []);

  // Get batch stock data on load
  useEffect(() => {
    getValues();
  }, []);

  // Process permissions
  const processPermissions = (roles) => {
    const permissionsMap = {};
    roles.forEach((role) => {
      role.modules.forEach((module) => {
        permissionsMap[module.moduleName] = module.permissions;
      });
    });
    return permissionsMap;
  };

  const hasPermission = (moduleName, action) => {
    return permissions[moduleName]?.[action] || false;
  };

  // Toggle sidebar menu
  const toggleMenu = () => {
    setIsMenuExpanded(!isMenuExpanded);
  };

  // Get batch stock data
  const getValues = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${BATCH_STOCK}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'DepartmentID': localStorage.getItem('department_id') || ''
        },
      });


      if (response.status === 200) {
        const stockData = (response.data.data).filter(item => item.ItemType === 'Pharmacy');
        setValues(stockData);
        
        // Calculate stock summary
        if (stockData.length > 0) {
          const totalStock = stockData.reduce((sum, item) => sum + (parseInt(item.Onhand) || 0), 0);
          const lowStockItems = stockData.filter(item => (parseInt(item.Onhand) || 0) <= 10).length;
          
          // Calculate expiring items (within 30 days)
          const today = new Date();
          const expiringItems = stockData.filter(item => {
            if (!item.ExpiryDate) return false;
            const expiry = new Date(item.ExpiryDate);
            const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
            return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
          }).length;
          
          setStockSummary({
            totalItems: stockData.length,
            totalStock,
            lowStockItems,
            expiringItems
          });
        } else {
          setStockSummary({
            totalItems: 0,
            totalStock: 0,
            lowStockItems: 0,
            expiringItems: 0
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch batch stock data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle filter change
  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  // Format expiry date to MM-YYYY format
  const formatExpiryDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear().toString();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      return `${month}-${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Toggle item expansion
  const toggleItemExpansion = (itemName) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemName)) {
        newSet.delete(itemName);
      } else {
        newSet.add(itemName);
      }
      return newSet;
    });
  };

  // Filter batch stock data based on search query and filter
  const filteredBatchStock = values.filter((row) => {
    // Zero stock filter - exclude zero stock items when checked
    const stockQty = parseInt(row.Onhand) || 0;
    if (excludeZeroStock && stockQty === 0) {
      return false;
    }
    
    // Search filter
    const query = searchQuery.trim().toLowerCase();
    const itemName = row.Item_Name?.toLowerCase() || '';
    const batchCode = row.BatchCode?.toString().toLowerCase() || '';
    const matchesSearch = itemName.includes(query) || batchCode.includes(query);
    
    // Status filter
    if (selectedFilter === 'Low Stock') {
      return stockQty <= 10 && matchesSearch;
    } else if (selectedFilter === 'Expiring Soon') {
      if (!row.ExpiryDate) return false;
      const today = new Date();
      const expiry = new Date(row.ExpiryDate);
      const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry > 0 && daysUntilExpiry <= 30 && matchesSearch;
    } else if (selectedFilter === 'Expired') {
      if (!row.ExpiryDate) return false;
      const today = new Date();
      const expiry = new Date(row.ExpiryDate);
      return expiry < today && matchesSearch;
    } else {
      return matchesSearch;
    }
  });

  // Group batch stock by item name
  const groupByItem = (data) => {
    const grouped = {};
    data.forEach(row => {
      const itemName = row.Item_Name || 'Unknown';
      if (!grouped[itemName]) {
        grouped[itemName] = [];
      }
      grouped[itemName].push(row);
    });
    return grouped;
  };

  // Calculate total stock for an item
  const calculateTotalStock = (batches) => {
    return batches.reduce((sum, batch) => sum + (parseInt(batch.Onhand) || 0), 0);
  };

  // Get grouped and sorted items
  const groupedItems = useMemo(() => {
    const grouped = groupByItem(filteredBatchStock);
    return Object.keys(grouped)
      .sort()
      .map(itemName => ({
        itemName,
        batches: grouped[itemName],
        totalStock: calculateTotalStock(grouped[itemName])
      }));
  }, [filteredBatchStock]);

  // Download options
  // const handleDownload = (format) => {
  //   // Implementation for download functionality
  //   toast.info(`${format} download initiated`, {
  //     autoClose: 1000,
  //     icon: <FiDownload className="text-indigo-500" />
  //   });
  //   setShowDownloadOptions(false);
  // };

  // Refresh data
  const handleRefresh = () => {
    getValues();
  };

  // Prepare export data (flattened batches, respecting filters)
  const getExportData = () => {
    let exportData = [];
    
    // Get all batches from grouped items
    groupedItems.forEach(item => {
      item.batches.forEach(batch => {
        const stockQty = parseInt(batch.Onhand) || 0;
        
        // Apply exclude zero stock filter if enabled
        if (excludeZeroStock && stockQty === 0) {
          return; // Skip this batch
        }
        
        exportData.push({
          'Item Name': item.itemName || 'N/A',
          'Batch Code': batch.BatchCode || 'N/A',
          'Stock Level': stockQty,
          'Expiry Date': batch.ExpiryDate ? formatExpiryDate(batch.ExpiryDate) : 'N/A',
          'Purchase Date': batch.PurchaseDate ? batch.PurchaseDate.split('T')[0] : 'N/A',
          'Purchase No.': batch.PurchaseRefNo || 'N/A',
        });
      });
    });
    
    return exportData;
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      const exportData = getExportData();
      
      if (exportData.length === 0) {
        toast.error('No batch stock data to export', { autoClose: 2000 });
        return;
      }

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const colWidths = [
        { wch: 30 }, // Item Name
        { wch: 15 }, // Batch Code
        { wch: 12 }, // Stock Level
        { wch: 12 }, // Expiry Date
        { wch: 15 }, // Purchase Date
        { wch: 15 }, // Purchase No.
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Batch Stock');

      // Generate filename
      const dateStr = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
      const filterStr = excludeZeroStock ? '_NoZeroStock' : '';
      const filename = `Batch_Stock_Report_${dateStr}${filterStr}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      toast.success('Excel file exported successfully', { autoClose: 2000 });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel', { autoClose: 2000 });
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const exportData = getExportData();
      
      if (exportData.length === 0) {
        toast.error('No batch stock data to export', { autoClose: 2000 });
        return;
      }

      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Batch Stock Report', pageWidth / 2, 15, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, 22, { align: 'center' });
      doc.text(`Total Batches: ${exportData.length}`, pageWidth / 2, 28, { align: 'center' });
      if (excludeZeroStock) {
        doc.text('Zero Stock Items Excluded', pageWidth / 2, 34, { align: 'center' });
      }

      // Prepare table data
      const tableData = exportData.map(item => [
        item['Item Name'],
        item['Batch Code'],
        item['Stock Level'].toString(),
        item['Expiry Date'],
        item['Purchase Date'],
        item['Purchase No.'],
      ]);

      // Create table
      autoTable(doc, {
        head: [['Item Name', 'Batch Code', 'Stock Level', 'Expiry Date', 'Purchase Date', 'Purchase No.']],
        body: tableData,
        startY: excludeZeroStock ? 40 : 35,
        styles: { 
          fontSize: 8, 
          cellPadding: 3,
          overflow: 'linebreak',
          cellWidth: 'wrap'
        },
        headStyles: { 
          fillColor: [99, 102, 241], 
          textColor: 255, 
          fontStyle: 'bold',
          fontSize: 9
        },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { top: excludeZeroStock ? 40 : 35, left: 10, right: 10 },
        tableWidth: 'wrap',
      });

      // Generate filename
      const dateStr = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
      const filterStr = excludeZeroStock ? '_NoZeroStock' : '';
      const filename = `Batch_Stock_Report_${dateStr}${filterStr}.pdf`;

      // Save file
      doc.save(filename);
      toast.success('PDF file exported successfully', { autoClose: 2000 });
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF', { autoClose: 2000 });
    }
  };

  return (
    <div className="flex h-full bg-background">
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      {loading && <Spinner />}
      
      {/* <LeftMenu isExpanded={isMenuExpanded} toggleMenu={toggleMenu} /> */}
      
      <div className="p-4 flex-1 flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-md p-3 mb-3">
          <div className="flex justify-between items-center">
            <h1 className="text-lg font-bold text-black flex items-center"> Batch Stock Management
            </h1>
            
            <div className="flex items-center space-x-3">
              <button 
                onClick={handleRefresh}
                className="p-2.5 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                title="Refresh data"
              >
                <FiRefreshCw size={18} />
              </button>
              
              {/* Download Options */}
              <div className="relative">
                <button
                  onClick={() => setShowDownloadOptions(!showDownloadOptions)}
                  className="p-2.5 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors flex items-center gap-2"
                  title="Export options"
                >
                  <FiDownload size={18} />
                  <FiChevronDown size={14} />
                </button>
                
                {showDownloadOptions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <button
                      onClick={() => {
                        exportToExcel();
                        setShowDownloadOptions(false);
                      }}
                      className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center gap-2 transition-colors"
                    >
                      <FiDownload size={16} />
                      Export to Excel
                    </button>
                    <button
                      onClick={() => {
                        exportToPDF();
                        setShowDownloadOptions(false);
                      }}
                      className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 flex items-center gap-2 transition-colors border-t border-gray-200"
                    >
                      <FiDownload size={16} />
                      Export to PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
        </div>
        
        {/* Summary Cards */}
        {values.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-md p-5">
              <div className="flex items-center">
                <div className="rounded-full bg-indigo-100 p-3 mr-4">
                  <FiBox className="text-indigo-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-black">Total Items</p>
                  <h3 className="text-2xl font-bold text-black">{stockSummary.totalItems}</h3>
                </div>
              </div>
            </div>
            
            
            <div className="bg-white rounded-xl shadow-md p-5">
              <div className="flex items-center">
                <div className="rounded-full bg-amber-100 p-3 mr-4">
                  <FiAlertCircle className="text-amber-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-black">Low Stock Items</p>
                  <h3 className="text-2xl font-bold text-black">{stockSummary.lowStockItems}</h3>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-5">
              <div className="flex items-center">
                <div className="rounded-full bg-blue-100 p-3 mr-4">
                  <FiCalendar className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-black">Expiring Soon</p>
                  <h3 className="text-2xl font-bold text-black">{stockSummary.expiringItems}</h3>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <div className="flex flex-wrap justify-between items-center">
            {/* Tabs */}
            <div className="flex space-x-1 mb-4 md:mb-0">
              <button
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedFilter === 'All' 
                    ? 'bg-indigo-100 text-indigo-700' 
                    : 'text-black hover:bg-gray-100'
                }`}
                onClick={() => handleFilterChange('All')}
              >
                <span className="flex items-center">
                  <FiFilter className="mr-2" />
                  All Stock
                </span>
              </button>
              
              <button
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedFilter === 'Low Stock' 
                    ? 'bg-amber-100 text-amber-700' 
                    : 'text-black hover:bg-gray-100'
                }`}
                onClick={() => handleFilterChange('Low Stock')}
              >
                <span className="flex items-center">
                  <FiAlertCircle className="mr-2" />
                  Low Stock
                </span>
              </button>
              
              <button
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedFilter === 'Expiring Soon' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-black hover:bg-gray-100'
                }`}
                onClick={() => handleFilterChange('Expiring Soon')}
              >
                <span className="flex items-center">
                  <FiClock className="mr-2" />
                  Expiring Soon
                </span>
              </button>
              
              <button
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedFilter === 'Expired' 
                    ? 'bg-red-100 text-red-700' 
                    : 'text-black hover:bg-gray-100'
                }`}
                onClick={() => handleFilterChange('Expired')}
              >
                <span className="flex items-center">
                  <FiInfo className="mr-2" />
                  Expired
                </span>
              </button>
            </div>
            
            {/* Search and Zero Stock Filter */}
            <div className="flex items-center gap-4 w-full md:w-auto">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={excludeZeroStock}
                  onChange={(e) => setExcludeZeroStock(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                />
                <span className="ml-2 text-sm text-black font-medium">Exclude Zero Stock</span>
              </label>
              
              <div className="relative w-full md:w-72">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-black">
                  <FiSearch size={16} />
                </div>
                <input
                  type="text"
                  placeholder="Search by item name or batch code"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Results Count */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-black">
            {groupedItems.length} {groupedItems.length === 1 ? 'item' : 'items'} found
            {searchQuery && ` for "${searchQuery}"`}
            {selectedFilter !== 'All' && ` with status "${selectedFilter}"`}
          </div>
        </div>
        
        {/* Accordion View */}
        <div className="bg-white rounded-xl shadow-md flex-1 overflow-hidden flex flex-col">
          {groupedItems.length > 0 ? (
            <div className="overflow-y-auto flex-1">
              <div className="border-b border-gray-200 sticky top-0 bg-gray-50 z-10">
                <div className="grid grid-cols-12 gap-4 px-6 py-3 text-xs font-medium text-black uppercase tracking-wider">
                  <div className="col-span-1"></div>
                  <div className="col-span-4">Item Name</div>
                  <div className="col-span-2">Total Stock</div>
                  <div className="col-span-2">Batch Code</div>
                  <div className="col-span-1">Expiry</div>
                  <div className="col-span-1">Stock</div>
                  <div className="col-span-1">Purchase Date</div>
                </div>
              </div>
              
              <div className="divide-y divide-gray-100">
                {groupedItems.map((item, itemIndex) => {
                  const isExpanded = expandedItems.has(item.itemName);
                  
                  return (
                    <div key={itemIndex} className="bg-white">
                      {/* Item Header Row */}
                      <div 
                        className={`grid grid-cols-12 gap-4 px-6 py-4 items-center cursor-pointer hover:bg-indigo-50 transition-colors ${
                          itemIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                        onClick={() => toggleItemExpansion(item.itemName)}
                      >
                        <div className="col-span-1 flex items-center">
                          {isExpanded ? (
                            <FiChevronDown className="text-indigo-600" size={18} />
                          ) : (
                            <FiChevronRight className="text-indigo-600" size={18} />
                          )}
                        </div>
                        <div className="col-span-4 text-sm font-medium text-black">
                          {item.itemName}
                        </div>
                        <div className="col-span-2 text-sm font-bold text-indigo-600">
                          {item.totalStock}
                        </div>
                        <div className="col-span-2 text-sm text-gray-500">
                          {item.batches.length} {item.batches.length === 1 ? 'batch' : 'batches'}
                        </div>
                        <div className="col-span-3"></div>
                      </div>
                      
                      {/* Expanded Batches */}
                      {isExpanded && (
                        <div className="bg-gray-50 border-t border-gray-200">
                          <div className="px-6 py-2">
                            <table className="w-full">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-black uppercase tracking-wider">Batch Code</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-black uppercase tracking-wider">Expiry Date</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-black uppercase tracking-wider">Stock Level</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-black uppercase tracking-wider">Purchase Date</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-black uppercase tracking-wider">Purchase No.</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {item.batches.map((batch, batchIndex) => (
                                  <tr 
                                    key={batchIndex}
                                    className="hover:bg-white transition-colors"
                                  >
                                    <td className="px-4 py-3 text-sm text-indigo-600 font-medium">
                                      {batch?.BatchCode || 'N/A'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-black">
                                      {formatExpiryDate(batch?.ExpiryDate)}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-black">
                                      {batch?.Onhand || 0}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-black">
                                      {batch?.PurchaseDate ? batch.PurchaseDate.split('T')[0] : 'N/A'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-black">
                                      {batch?.PurchaseRefNo || 'N/A'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <NoData />
          )}
        </div>
      </div>
    </div>
  );
}

export default withAuth(BatchStock);